/**
 * Pestaña "Historial de Estados" a nivel de proyecto.
 * Muestra, en orden cronológico, cada cambio de estado del proyecto con
 * fecha y hora exactas y el usuario que lo realizó (empezando por el primer
 * estado registrado). Siempre visible, sin importar el estado actual.
 */
import React, { useEffect, useState } from 'react';
import client from '../../../services/graphqlClient';
import './HistorialEstados.css';

// Etiqueta y color por cada estado del sistema
const ESTADO_INFO = {
  vendido:                 { label: 'Vendido',                  color: '#2563eb' },
  contratado_no_iniciado:  { label: 'Contratado (no iniciado)', color: '#64748b' },
  en_ejecucion:            { label: 'En ejecución',             color: '#d97706' },
  entregado_con_detalles:  { label: 'Entregado con detalles',   color: '#7c3aed' },
  cobrado_liquidado:       { label: 'Cobrado / Liquidado',      color: '#0d9488' },
  entregado_liquidado:     { label: 'Entregado / Liquidado',    color: '#16a34a' },
  posventa:                { label: 'Posventa',                 color: '#db2777' },
  proyecto_especial:       { label: 'Proyecto especial',        color: '#4f46e5' },
};

const infoEstado = (e) => ESTADO_INFO[e] || { label: e || '—', color: '#94a3b8' };

const formatFechaHora = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const fecha = d.toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' });
  const hora = d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${fecha} · ${hora}`;
};

const EstadoBadge = ({ estado }) => {
  const { label, color } = infoEstado(estado);
  return (
    <span className="he-badge" style={{ backgroundColor: `${color}1a`, color, borderColor: `${color}55` }}>
      {label}
    </span>
  );
};

export default function HistorialEstados({ proyectoId }) {
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let activo = true;
    (async () => {
      setCargando(true);
      try {
        const data = await client.query(
          `query($pid: Int!) {
            proyectoEstadoHistorial(proyecto_id: $pid) {
              id estado_anterior estado_nuevo usuario_nombre nota fecha_cambio
            }
          }`,
          { pid: proyectoId }
        );
        if (activo) setItems(data.proyectoEstadoHistorial || []);
      } catch (e) {
        console.error('Error cargando historial de estados:', e);
        if (activo) setItems([]);
      } finally {
        if (activo) setCargando(false);
      }
    })();
    return () => { activo = false; };
  }, [proyectoId]);

  return (
    <div className="he-container">
      <div className="he-header">
        <h2>Historial de Estados</h2>
        <p>Registro cronológico de cada cambio de estado: cuándo ocurrió y quién lo realizó.</p>
      </div>

      {cargando ? (
        <div className="he-vacio">Cargando historial…</div>
      ) : items.length === 0 ? (
        <div className="he-vacio">Aún no hay cambios de estado registrados para este proyecto.</div>
      ) : (
        <ul className="he-timeline">
          {items.map((it, idx) => {
            const { color } = infoEstado(it.estado_nuevo);
            const esPrimero = idx === 0;
            return (
              <li className="he-item" key={it.id}>
                <span className="he-dot" style={{ backgroundColor: color }} />
                <div className="he-card">
                  <div className="he-card-top">
                    <div className="he-transicion">
                      {it.estado_anterior && !esPrimero ? (
                        <>
                          <EstadoBadge estado={it.estado_anterior} />
                          <span className="he-flecha">→</span>
                          <EstadoBadge estado={it.estado_nuevo} />
                        </>
                      ) : (
                        <>
                          <span className="he-inicial-label">Estado inicial</span>
                          <EstadoBadge estado={it.estado_nuevo} />
                        </>
                      )}
                    </div>
                    <span className="he-num">#{idx + 1}</span>
                  </div>
                  <div className="he-meta">
                    <span className="he-fecha">🕒 {formatFechaHora(it.fecha_cambio)}</span>
                    <span className="he-usuario">👤 {it.usuario_nombre || 'Sistema'}</span>
                  </div>
                  {it.nota && <div className="he-nota">{it.nota}</div>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
